import moment from 'moment';
// @ts-expect-error -- moment locale files have no type declarations
import 'moment/dist/locale/ru';
import { useCallback, useEffect, useMemo, useState } from 'react';

import './period.scss';

import { lockAll, updateLocks, updatePeriods } from '~modules/period/api';
import { LockType, SectionWithPeriodDto } from '~modules/period/dto';
import { Preloader, toastError, toastSuccess } from '~shared/ui';
import { CheckBox } from '~shared/ui';
import { Button } from '~shared/ui/button/button';
import { SaveBtn } from '~shared/ui/button/save-btn';
import { InputItem } from '~shared/ui/input-item/input-item';

import { usePeriods } from './use-periods';

// moment.locale('ru');

export function PeriodPage() {
	const [cursor, setCursor] = useState(() => moment().locale('ru').startOf('month'));
	const year = cursor.year();
	const month = cursor.month() + 1;

	const { list, pending, reload } = usePeriods(year, month);
	const [periods, setPeriods] = useState<SectionWithPeriodDto[]>([]);

	useEffect(() => {
		setPeriods(list);
	}, [list]);

	const handlePeriodChange = useCallback((sectionId: number, newPeriod: number | null) => {
		setPeriods(prev =>
			prev.map(section => (section.id === sectionId ? { ...section, period: newPeriod } : section)),
		);
	}, []);

	const handleInputChange = useCallback(
		(section: SectionWithPeriodDto) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const periodValue = e.target.value;
			const period = periodValue === '' ? null : parseInt(periodValue) || 0;
			handlePeriodChange(section.id!, period);
		},
		[handlePeriodChange],
	);

	const handleLockToggle = (sectionId: number, type: LockType) => {
		setPeriods(prev =>
			prev.map(section => {
				if (section.id !== sectionId) return section;
				if (type === 'advance') return { ...section, advance_locked: !section.advance_locked };
				return { ...section, salary_locked: !section.salary_locked };
			}),
		);
	};

	const handleSave = async () => {
		try {
			await updatePeriods(periods.map(v => ({ section_id: v.id!, period: v.period ?? null })));
			await updateLocks(
				periods.map(v => ({
					section_id: v.id!,
					year,
					month,
					advance_locked: !!v.advance_locked,
					salary_locked: !!v.salary_locked,
				})),
			);
			toastSuccess('Настройки периода обновлены');
		} catch (err) {
			if (err instanceof Error) toastError(err.message);
			else console.error(err);
		}
	};

	const allAdvanceLocked = useMemo(() => periods.length > 0 && periods.every(s => s.advance_locked), [periods]);
	const allSalaryLocked = useMemo(() => periods.length > 0 && periods.every(s => s.salary_locked), [periods]);

	const handleBulkLock = async (type: LockType, locked: boolean) => {
		try {
			await lockAll(year, month, type, locked);
			toastSuccess(locked ? 'Период заблокирован' : 'Период разблокирован');
			reload();
		} catch (err) {
			if (err instanceof Error) toastError(err.message);
			else console.error(err);
		}
	};

	const goPrev = () => setCursor(prev => prev.clone().subtract(1, 'month').startOf('month'));
	const goNext = () => setCursor(prev => prev.clone().add(1, 'month').startOf('month'));

	const renderPeriodItem = (section: SectionWithPeriodDto) => (
		<tr key={section.id}>
			<td>
				<InputItem value={section.title ?? ''} disabled />
			</td>
			<td>
				<InputItem
					placeholder="0"
					min="0"
					type="number"
					value={section.period ?? ''}
					onChange={handleInputChange(section)}
				/>
			</td>
			<td className="period-page__lock-cell">
				<CheckBox
					checked={!!section.advance_locked}
					onChange={() => handleLockToggle(section.id!, 'advance')}
				/>
			</td>
			<td className="period-page__lock-cell">
				<CheckBox checked={!!section.salary_locked} onChange={() => handleLockToggle(section.id!, 'salary')} />
			</td>
		</tr>
	);

	return (
		<div className="period-page page">
			{pending ? (
				<Preloader style={{ margin: '40px 0' }} />
			) : (
				<div className="page-container">
					<div className="period-page__toolbar">
						<h2 className="page-title" style={{ margin: 0 }}>
							Период табелирования
						</h2>
						<div className="period-page__bulk">
							<Button
								variant={allAdvanceLocked ? 'outline' : 'solid'}
								onClick={() => handleBulkLock('advance', !allAdvanceLocked)}
							>
								{allAdvanceLocked ? 'Разблокировать Аванс' : 'Заблокировать Аванс'}
							</Button>
							<Button
								variant={allSalaryLocked ? 'outline' : 'solid'}
								onClick={() => handleBulkLock('salary', !allSalaryLocked)}
							>
								{allSalaryLocked ? 'Разблокировать З/П' : 'Заблокировать З/П'}
							</Button>
						</div>
						<div className="period-page__month-nav">
							<button
								type="button"
								className="period-page__nav-btn"
								onClick={goPrev}
								aria-label="Предыдущий месяц"
							>
								←
							</button>
							<span className="period-page__month-label">{cursor.locale('fr').format('MMMM YYYY')}</span>
							<button
								type="button"
								className="period-page__nav-btn"
								onClick={goNext}
								aria-label="Следующий месяц"
							>
								→
							</button>
						</div>
					</div>
					<table className="table-for-tabel">
						<thead>
							<tr>
								<th>Участок</th>
								<th>Период (в днях)</th>
								<th>Аванс</th>
								<th>Зарплата</th>
							</tr>
						</thead>
						<tbody>{periods.map(renderPeriodItem)}</tbody>
					</table>
					<SaveBtn style={{ margin: '20px auto 0' }} handleSave={handleSave} />
				</div>
			)}
		</div>
	);
}
